// ============================================
// FAVORITE NOTE MODAL COMPONENT
// ============================================

import { useState, useEffect } from 'react';
import { X, Tag, FileText, Loader2, Check } from 'lucide-react';
import { cn } from '@/utils';
import { useUpdateFavoriteCandidate } from '@/hooks';
import { useTranslation } from '@crew-at-sea/i18n';

interface FavoriteNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  seafarerId: string;
  seafarerName: string;
  initialNotes?: string;
  initialTags?: string[];
  onSuccess?: () => void;
}

// Preset tag key'leri — çeviri panel:favorites.presetTags.* ile yapılıyor
const PRESET_TAG_KEYS = [
  'potential',
  'experienced',
  'newGrad',
  'urgent',
  'onHold',
  'interviewed',
  'offered',
  'recommended',
] as const;

export function FavoriteNoteModal({
  isOpen,
  onClose,
  seafarerId,
  seafarerName,
  initialNotes = '',
  initialTags = [],
  onSuccess,
}: FavoriteNoteModalProps) {
  const { t } = useTranslation(['panel', 'common']);
  const [notes, setNotes] = useState(initialNotes);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [customTag, setCustomTag] = useState('');
  
  const updateMutation = useUpdateFavoriteCandidate();

  // Preset tag'lerin çevirilmiş halleri
  const presetTags = PRESET_TAG_KEYS.map((key) => t(`panel:favorites.presetTags.${key}`));

  useEffect(() => {
    if (isOpen) {
      setNotes(initialNotes);
      setTags(initialTags);
      setCustomTag('');
    }
  }, [isOpen, initialNotes, initialTags]);

  const toggleTag = (tag: string) => {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const addCustomTag = () => {
    if (customTag.trim() && !tags.includes(customTag.trim())) {
      setTags((prev) => [...prev, customTag.trim()]);
      setCustomTag('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await updateMutation.mutateAsync({
        seafarerId,
        notes: notes.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update favorite:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-lg bg-white rounded-maritime shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('panel:favorites.editNote')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">{seafarerName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* İçerik */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              {/* Notlar */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4" />
                  {t('panel:favorites.notes')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('panel:favorites.notesPlaceholder')}
                  rows={4}
                  className="input w-full resize-none"
                />
              </div>

              {/* Etiketler */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Tag className="w-4 h-4" />
                  {t('panel:favorites.tags')}
                </label>
                
                {/* Hazır etiketler */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {presetTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        'px-3 py-1.5 text-sm rounded-full border transition-colors',
                        tags.includes(tag)
                          ? 'bg-maritime-100 text-maritime-700 border-maritime-300'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'
                      )}
                    >
                      {tags.includes(tag) && <Check className="w-3 h-3 inline-block mr-1" />}
                      {tag}
                    </button>
                  ))}
                </div>

                {/* Seçilen özel etiketler */}
                {tags.filter((t) => !presetTags.includes(t)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {tags
                      .filter((t) => !presetTags.includes(t))
                      .map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full bg-purple-100 text-purple-700 border border-purple-200"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => toggleTag(tag)}
                            className="hover:text-purple-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}

                {/* Özel etiket input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customTag}
                    onChange={(e) => setCustomTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomTag();
                      }
                    }}
                    placeholder={t('panel:favorites.customTagPlaceholder')}
                    className="input flex-1"
                    maxLength={30}
                  />
                  <button
                    type="button"
                    onClick={addCustomTag}
                    disabled={!customTag.trim()}
                    className="btn-secondary"
                  >
                    {t('common:add')}
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button type="button" onClick={onClose} className="btn-secondary">
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="btn-primary inline-flex items-center gap-2"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common:saving')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t('common:save')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
